// Convolution: apply an impulse response to a source signal. Renders
// through OfflineAudioContext + ConvolverNode — the browser's optimised
// FFT-based implementation, far faster than naive time-domain convolution
// for any non-trivial IR length.
//
// Pure DSP — no storage, no provenance. The wrapper in mangleEffects.ts
// loads an IR sample from IDB and calls into here; the standalone verb
// also calls into here with two already-decoded buffers.

import type { PcmBuffer } from '../../pipeline';

export type ConvolveOptions = {
  /** Web Audio's normalize flag — true scales the IR so quiet IRs are
   *  audible without manual gain tweaks. Default true. */
  normalize?: boolean;
};

export async function applyConvolve(
  source: PcmBuffer,
  ir: PcmBuffer,
  opts: ConvolveOptions = {},
): Promise<PcmBuffer> {
  const Ctor = getOfflineCtor();
  if (!Ctor) return source; // test fallback: passthrough
  const nCh = Math.max(1, source.channels.length);
  const inLen = source.channels[0]?.length ?? 0;
  if (inLen === 0) return source;
  const irLen = ir.channels[0]?.length ?? 0;
  if (irLen === 0) return source;
  // Convolution lengthens the signal by ~the length of the IR. We
  // accept this — the trim chain in the forge shell lets the user
  // shorten if they want.
  const outLen = inLen + irLen;
  const rCtx = new Ctor(nCh, outLen, source.sampleRate);

  const srcBuf = rCtx.createBuffer(nCh, inLen, source.sampleRate);
  for (let c = 0; c < nCh; c++) {
    const copy = new Float32Array(source.channels[c]!.length);
    copy.set(source.channels[c]!);
    srcBuf.copyToChannel(copy, c);
  }

  // Decode the IR into a Web Audio buffer too. ConvolverNode handles any
  // channel count — mono IR on stereo source = symmetric reverb, stereo
  // IR on mono source = stereo widening, etc.
  const irNCh = Math.max(1, ir.channels.length);
  const irBuf = rCtx.createBuffer(irNCh, irLen, ir.sampleRate);
  for (let c = 0; c < irNCh; c++) {
    const copy = new Float32Array(ir.channels[c]!.length);
    copy.set(ir.channels[c]!);
    irBuf.copyToChannel(copy, c);
  }

  const src = rCtx.createBufferSource();
  src.buffer = srcBuf;
  const conv = rCtx.createConvolver();
  conv.normalize = opts.normalize ?? true;
  conv.buffer = irBuf;
  src.connect(conv);
  conv.connect(rCtx.destination);
  src.start(0);
  const rendered = await rCtx.startRendering();
  return {
    sampleRate: source.sampleRate,
    channels: Array.from({ length: rendered.numberOfChannels }, (_, c) => {
      const copy = new Float32Array(rendered.length);
      copy.set(rendered.getChannelData(c));
      return copy;
    }),
  };
}

type OfflineAudioContextCtor = new (
  channels: number,
  length: number,
  sampleRate: number,
) => OfflineAudioContext;

function getOfflineCtor(): OfflineAudioContextCtor | null {
  if (typeof OfflineAudioContext !== 'undefined') return OfflineAudioContext;
  const webkit = (globalThis as unknown as {
    webkitOfflineAudioContext?: OfflineAudioContextCtor;
  }).webkitOfflineAudioContext;
  return typeof webkit === 'function' ? webkit : null;
}
